import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, User, Phone, Mail, MapPin, Clock,
  CheckCircle2, XCircle, AlertCircle, Plus, Edit, Trash2,
  ChevronLeft, ChevronRight, Loader2, CalendarDays
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Progress } from '../../../components/ui/progress';
import { companyApi } from '../../../lib/api';
import { format, parseISO, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// Status colors and labels
const BOOKING_STATUS = {
  pending_validation: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', dotColor: 'bg-yellow-500' },
  confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-800', dotColor: 'bg-blue-500' },
  in_progress: { label: 'En cours', color: 'bg-cyan-100 text-cyan-800', dotColor: 'bg-cyan-500' },
  completed: { label: 'Réalisé', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800', dotColor: 'bg-red-500' },
};

export function SubscriptionDetailPage() {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [client, setClient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Dialog states
  const [isAddDateDialogOpen, setIsAddDateDialogOpen] = useState(false);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newPeriod, setNewPeriod] = useState('morning');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, collabRes] = await Promise.all([
        companyApi.getSubscriptionStats(subscriptionId),
        companyApi.getCollaborators()
      ]);
      
      setSubscription(statsRes.data.subscription);
      setClient(statsRes.data.client);
      setBookings(statsRes.data.bookings);
      setStats(statsRes.data.stats);
      setCollaborators(collabRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [subscriptionId]);

  const handleAddDate = async () => {
    if (!newDate) return;
    
    setIsSubmitting(true);
    try {
      await companyApi.addBookingToSubscription(subscriptionId, newDate, 'half_day', newPeriod);
      toast.success('Date ajoutée');
      setIsAddDateDialogOpen(false);
      setNewDate('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedBooking || !newDate) return;
    
    setIsSubmitting(true);
    try {
      await companyApi.rescheduleBooking(selectedBooking.id, newDate, newPeriod);
      toast.success('Date modifiée');
      setIsRescheduleDialogOpen(false);
      setSelectedBooking(null);
      setNewDate('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleValidateBooking = async (bookingId) => {
    try {
      await companyApi.validateBooking(bookingId, {});
      toast.success('Réservation confirmée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await companyApi.updateBookingStatus(bookingId, 'cancelled');
      toast.success('Réservation annulée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.filter(b => b.date === dateStr);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Abonnement non trouvé</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>
    );
  }

  const progressPercent = stats ? Math.round((stats.completed / (stats.total || 1)) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold">
              Abonnement #{subscription.id?.slice(0, 8)}
            </h1>
            <p className="text-muted-foreground">
              {client?.name || 'Client'}
            </p>
          </div>
        </div>
        
        <Button onClick={() => setIsAddDateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une date
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-primary">
              {stats?.completed || 0} / {stats?.total || 0}
            </div>
            <p className="text-sm text-muted-foreground">Passages réalisés</p>
            <Progress value={progressPercent} className="mt-2 h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-blue-600">{stats?.confirmed || 0}</div>
            <p className="text-sm text-muted-foreground">Confirmés</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-yellow-600">{stats?.pending || 0}</div>
            <p className="text-sm text-muted-foreground">En attente</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-green-600">{stats?.remaining || 0}</div>
            <p className="text-sm text-muted-foreground">Restants</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Left: Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {client ? (
              <>
                <div>
                  <p className="font-medium">{client.name}</p>
                </div>
                {client.email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    {client.email}
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    {client.phone}
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {client.address}
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">-</p>
            )}
            
            <hr className="my-4" />
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Fréquence</p>
              <p className="font-medium">{subscription.frequency || '-'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Type de créneau</p>
              <p className="font-medium">
                {subscription.slot_type === 'full_day' ? 'Journée complète' : 'Demi-journée'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Right: Calendar + Bookings List */}
        <div className="md:col-span-2 space-y-4">
          {/* Mini Calendar */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Planning des passages
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium min-w-[140px] text-center">
                    {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                  </span>
                  <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                  <div key={day} className="font-medium text-muted-foreground py-2">{day}</div>
                ))}
                
                {/* Empty cells for days before month start */}
                {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
                  <div key={`empty-${i}`} className="p-2" />
                ))}
                
                {/* Calendar days */}
                {calendarDays.map(day => {
                  const dayBookings = getBookingsForDate(day);
                  const hasBooking = dayBookings.length > 0;
                  const isPast = isBefore(day, new Date()) && !isToday(day);
                  
                  return (
                    <div 
                      key={day.toISOString()} 
                      className={`p-2 rounded-lg relative ${
                        isToday(day) ? 'bg-primary/10 font-bold' : ''
                      } ${isPast ? 'text-muted-foreground' : ''}`}
                    >
                      <span>{format(day, 'd')}</span>
                      {hasBooking && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                          {dayBookings.map(b => (
                            <div 
                              key={b.id}
                              className={`w-2 h-2 rounded-full ${BOOKING_STATUS[b.status]?.dotColor || 'bg-gray-400'}`}
                              title={BOOKING_STATUS[b.status]?.label}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 text-xs">
                {Object.entries(BOOKING_STATUS).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${config.dotColor}`} />
                    <span>{config.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bookings List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Liste des passages</CardTitle>
              <CardDescription>
                {stats?.completed || 0} réalisés sur {stats?.total || 0} programmés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {bookings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Aucun passage programmé
                  </p>
                ) : (
                  bookings.map(booking => {
                    const statusConfig = BOOKING_STATUS[booking.status] || BOOKING_STATUS.pending_validation;
                    const isPast = isBefore(parseISO(booking.date), new Date());
                    const canReschedule = booking.status !== 'completed' && booking.status !== 'cancelled';
                    
                    return (
                      <div 
                        key={booking.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          booking.status === 'completed' ? 'bg-green-50 border-green-200' :
                          booking.status === 'cancelled' ? 'bg-gray-50 border-gray-200 opacity-60' :
                          'bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <CalendarDays className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {format(parseISO(booking.date), 'EEEE d MMMM yyyy', { locale: fr })}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {booking.slot_period === 'morning' ? 'Matin' : 'Après-midi'}
                              {booking.collaborator_name && ` • ${booking.collaborator_name}`}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          
                          {booking.status === 'pending_validation' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600"
                              onClick={() => handleValidateBooking(booking.id)}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {canReschedule && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setNewDate(booking.date);
                                setNewPeriod(booking.slot_period || 'morning');
                                setIsRescheduleDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {canReschedule && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleCancelBooking(booking.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Date Dialog */}
      <Dialog open={isAddDateDialogOpen} onOpenChange={setIsAddDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une date</DialogTitle>
            <DialogDescription>
              Programmez un nouveau passage pour cet abonnement
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Période</Label>
              <Select value={newPeriod} onValueChange={setNewPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Matin</SelectItem>
                  <SelectItem value="afternoon">Après-midi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddDateDialogOpen(false)}>
                Annuler
              </Button>
              <Button className="flex-1" onClick={handleAddDate} disabled={!newDate || isSubmitting}>
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la date</DialogTitle>
            <DialogDescription>
              Reprogrammez ce passage à une nouvelle date
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedBooking && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Date actuelle</p>
                <p className="font-medium">
                  {format(parseISO(selectedBooking.date), 'EEEE d MMMM yyyy', { locale: fr })}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Nouvelle date *</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Période</Label>
              <Select value={newPeriod} onValueChange={setNewPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Matin</SelectItem>
                  <SelectItem value="afternoon">Après-midi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsRescheduleDialogOpen(false)}>
                Annuler
              </Button>
              <Button className="flex-1" onClick={handleReschedule} disabled={!newDate || isSubmitting}>
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Modifier'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SubscriptionDetailPage;
